<script lang="ts">
  import { fly } from 'svelte/transition';
  import { getAppearance } from '../themes/appearance.svelte';
  import Button from './Button.svelte';
  interface Props {
    id?: number;
    message?: string;
    good?: boolean;
    dismiss: () => void;
  }
  let { id = 0, message = '', good = false, dismiss }: Props = $props();
  const appearance = getAppearance();
  // No X button: the whole notice is the dismiss target (tap anywhere) and a
  // downward swipe also clears it. The slide runs only when the Motion opt-in
  // and the OS both allow it; otherwise notices appear and clear instantly.
  const slide = $derived(appearance.motionAllowed ? { y: 72, duration: 200 } : { y: 0, duration: 0 });
  /** A downward drag clears the notice; tap-to-dismiss is the button click, so the swipe frame is presentational. */
  const SWIPE_DISMISS_PX = 32;
  let swipeStart = $state<{ x: number; y: number } | null>(null);
  function onPointerDown(event: PointerEvent): void {
    swipeStart = { x: event.clientX, y: event.clientY };
  }
  function onPointerUp(event: PointerEvent): void {
    if (!swipeStart) return;
    const frame = event.currentTarget;
    const dx = event.clientX - swipeStart.x;
    const dy = event.clientY - swipeStart.y;
    swipeStart = null;
    // A scrolled long notice keeps its scroll gesture; only an unscrolled
    // downward drag dismisses.
    if (frame instanceof HTMLElement && frame.scrollTop > 8) return;
    if (dy > SWIPE_DISMISS_PX && dy > Math.abs(dx)) dismiss();
  }
</script>

<div class="toast-region" role="status" aria-live="polite" aria-atomic="true">
  {#key id}{#if message}<div
        class="surface notice"
        role="presentation"
        transition:fly={slide}
        onpointerdown={onPointerDown}
        onpointerup={onPointerUp}
      >
        <Button variant="ghost" class="notice-dismiss" aria-label="Dismiss notice" onclick={dismiss}
          ><span><strong>{good ? 'Success: ' : 'Notice: '}</strong>{message}</span></Button
        >
      </div>{/if}{/key}
</div>

<style>
  .toast-region {
    position: fixed;
    bottom: max(20px, env(safe-area-inset-bottom));
    left: 16px;
    right: 16px;
    z-index: 100;
    pointer-events: none;
  }
  .notice {
    pointer-events: auto;
    margin: auto;
    width: fit-content;
    max-width: min(100%, 560px);
    max-height: 40dvh;
    overflow: auto;
    padding: 0;
    touch-action: pan-y;
  }
  /* The shared seam renders the native control; only its fit inside the painted
     frame is app-owned, hence the deliberate global. */
  :global(.btn.notice-dismiss) {
    width: 100%;
    min-height: 44px;
    padding: 12px;
    font: inherit;
    letter-spacing: normal;
    text-transform: none;
    text-align: left;
    color: inherit;
  }
  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
</style>
